/**
 * React Query hooks for pressure control (api#1268, BOP-1). One key family,
 * `['pressure-control', …]`, so any write invalidates the register, the record
 * page and the Equipment-detail status panel together. Bodies are read through
 * lib/pressure-control.ts, which tolerates camelCase and snake_case.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap, unwrapArray } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import {
  readCalibration,
  readComponent,
  readComponentDetail,
  readDrill,
  readDrillStatus,
  readExtension,
  readGaugeCalibration,
  readModuleStatus,
  readTest,
  readTestDefault,
} from '../lib/pressure-control';
import type {
  PcCalibration,
  PcCalibrationWrite,
  PcClassRule,
  PcClassRuleRow,
  PcClassRuleWrite,
  PcDrill,
  PcDrillSettings,
  PcDrillWrite,
  PcExtension,
  PcExtensionWrite,
  PcTest,
  PcTestDefault,
  PcTestDefaultWrite,
  PcTestWrite,
  PcCertificationWrite,
  PcComponent,
  PcComponentWrite,
  PcEventWrite,
  PcMeta,
  PcResponseMeta,
  PcSettings,
  PcSettingsWrite,
} from '../types/pressure-control';

export const PC_KEY = ['pressure-control'] as const;

export function usePcMeta(enabled = true) {
  return useQuery({
    queryKey: [...PC_KEY, 'meta'],
    queryFn: async () => unwrap<PcMeta>((await api.pressureControl.meta()).data),
    staleTime: 60 * 60 * 1000,
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePcSettings(enabled = true) {
  return useQuery({
    queryKey: [...PC_KEY, 'settings'],
    queryFn: async () => unwrap<PcSettings>((await api.pressureControl.settings()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The register. `meta` carries the tenant's day and pressure unit. */
export function usePcComponents(enabled = true) {
  return useQuery({
    queryKey: [...PC_KEY, 'components'],
    queryFn: async () => {
      const body = (await api.pressureControl.components()).data as any;
      return {
        rows: unwrapArray<any>(body).map(readComponent).filter((c): c is PcComponent => c !== null),
        meta: (body?.meta ?? null) as PcResponseMeta | null,
      };
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePcComponent(equipmentId: string | undefined) {
  return useQuery({
    queryKey: [...PC_KEY, 'component', equipmentId],
    queryFn: async () => {
      const body = (await api.pressureControl.component(equipmentId!)).data as any;
      return { component: readComponentDetail(unwrap<any>(body)), meta: (body?.meta ?? null) as PcResponseMeta | null };
    },
    enabled: !!equipmentId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Equipment detail: the unit's status, or `component: null` when it is not a component. */
export function usePcEquipmentStatus(equipmentId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...PC_KEY, 'equipment-status', equipmentId],
    queryFn: async () => {
      const body = (await api.pressureControl.equipmentStatus(equipmentId!)).data as any;
      return { component: readComponent(unwrap<any>(body)), meta: (body?.meta ?? null) as PcResponseMeta | null };
    },
    enabled: !!equipmentId && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useInvalidatePc() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: PC_KEY });
}

export function useUpdatePcSettings() {
  const invalidate = useInvalidatePc();
  return useMutation({
    mutationFn: async (body: PcSettingsWrite) => unwrap<PcSettings>((await api.pressureControl.updateSettings(body)).data),
    onSuccess: invalidate,
  });
}

export function useCreatePcComponent() {
  const invalidate = useInvalidatePc();
  return useMutation({
    mutationFn: async (body: PcComponentWrite) => readComponent(unwrap<any>((await api.pressureControl.createComponent(body)).data)),
    onSuccess: invalidate,
  });
}

export function useUpdatePcComponent(equipmentId: string) {
  const invalidate = useInvalidatePc();
  return useMutation({
    mutationFn: async (body: PcComponentWrite) => readComponent(unwrap<any>((await api.pressureControl.updateComponent(equipmentId, body)).data)),
    onSuccess: invalidate,
  });
}

export function useRecordPcCertification(equipmentId: string) {
  const invalidate = useInvalidatePc();
  return useMutation({
    mutationFn: async (body: PcCertificationWrite) => (await api.pressureControl.recordCertification(equipmentId, body)).data,
    onSuccess: invalidate,
  });
}

export function useRecordPcEvent(equipmentId: string) {
  const invalidate = useInvalidatePc();
  return useMutation({
    mutationFn: async (body: PcEventWrite) => (await api.pressureControl.recordEvent(equipmentId, body)).data,
    onSuccess: invalidate,
  });
}

// ─── BOP-2 (api#1281): tests, drills, calibrations, defaults, extensions ─────
// Same key family, so recording a test, a calibration or an extension also
// refreshes the register's certificate states and the module status.

const metaOf = (body: any) => (body?.meta ?? null) as PcResponseMeta | null;

export function usePcStatus(enabled = true) {
  return useQuery({
    queryKey: [...PC_KEY, 'status'],
    queryFn: async () => {
      const body = (await api.pressureControl.status()).data as any;
      return { status: readModuleStatus(unwrap<any>(body)), meta: metaOf(body) };
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePcCalibrations(enabled = true) {
  return useQuery({
    queryKey: [...PC_KEY, 'calibrations'],
    queryFn: async () =>
      unwrapArray<any>((await api.pressureControl.calibrations()).data).map(readCalibration).filter((c): c is PcCalibration => c !== null),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePcGaugeCalibration(equipmentId: string | null | undefined) {
  return useQuery({
    queryKey: [...PC_KEY, 'gauge-calibration', equipmentId],
    queryFn: async () => readGaugeCalibration(unwrap<any>((await api.pressureControl.gaugeCalibration(equipmentId!)).data)),
    enabled: !!equipmentId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePcTests(params?: { equipment_id?: string }, enabled = true) {
  return useQuery({
    queryKey: [...PC_KEY, 'tests', params ?? {}],
    queryFn: async () =>
      unwrapArray<any>((await api.pressureControl.tests(params)).data).map(readTest).filter((t): t is PcTest => t !== null),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePcTest(id: string | undefined) {
  return useQuery({
    queryKey: [...PC_KEY, 'test', id],
    queryFn: async () => readTest(unwrap<any>((await api.pressureControl.test(id!)).data)),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePcDrills(enabled = true) {
  return useQuery({
    queryKey: [...PC_KEY, 'drills'],
    queryFn: async () =>
      unwrapArray<any>((await api.pressureControl.drills()).data).map(readDrill).filter((d): d is PcDrill => d !== null),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePcDrill(id: string | undefined) {
  return useQuery({
    queryKey: [...PC_KEY, 'drill', id],
    queryFn: async () => readDrill(unwrap<any>((await api.pressureControl.drill(id!)).data)),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePcDrillSettings(enabled = true) {
  return useQuery({
    queryKey: [...PC_KEY, 'drill-settings'],
    queryFn: async () => unwrap<PcDrillSettings>((await api.pressureControl.drillSettings()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePcTestDefaults(enabled = true) {
  return useQuery({
    queryKey: [...PC_KEY, 'test-defaults'],
    queryFn: async () =>
      unwrapArray<any>((await api.pressureControl.testDefaults()).data).map(readTestDefault).filter((d): d is PcTestDefault => d !== null),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePcExtensions(equipmentId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...PC_KEY, 'extensions', equipmentId],
    queryFn: async () =>
      unwrapArray<any>((await api.pressureControl.extensions(equipmentId!)).data).map(readExtension).filter((e): e is PcExtension => e !== null),
    enabled: !!equipmentId && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The defaults that pre-fill a test item for one component — fetched on demand, not as a hook. */
export async function fetchPcComponentTestDefaults(equipmentId: string): Promise<PcTestDefault | null> {
  return readTestDefault(unwrap<any>((await api.pressureControl.componentTestDefaults(equipmentId)).data));
}

export function useRecordPcTest() {
  const invalidate = useInvalidatePc();
  return useMutation({
    mutationFn: async (body: PcTestWrite) => {
      const res = (await api.pressureControl.recordTest(body)).data as any;
      return { test: readTest(unwrap<any>(res)), warnings: (Array.isArray(res?.warnings) ? res.warnings : []) as Array<{ equipmentId: string; code: string; message: string }> };
    },
    onSuccess: invalidate,
  });
}

export function useRecordPcDrill() {
  const invalidate = useInvalidatePc();
  return useMutation({
    mutationFn: async (body: PcDrillWrite) => {
      const data = unwrap<any>((await api.pressureControl.recordDrill(body)).data);
      return { drill: readDrill(data?.drill), status: readDrillStatus(data?.status) };
    },
    onSuccess: invalidate,
  });
}

export function useRecordPcCalibration() {
  const invalidate = useInvalidatePc();
  return useMutation({
    mutationFn: async (body: PcCalibrationWrite) => readCalibration(unwrap<any>((await api.pressureControl.recordCalibration(body)).data)),
    onSuccess: invalidate,
  });
}

export function useUpdatePcDrillSettings() {
  const invalidate = useInvalidatePc();
  return useMutation({
    mutationFn: async (body: { drill_warn_days: number }) => unwrap<PcDrillSettings>((await api.pressureControl.updateDrillSettings(body)).data),
    onSuccess: invalidate,
  });
}

export function usePutPcTestDefault() {
  const invalidate = useInvalidatePc();
  return useMutation({
    mutationFn: async (body: PcTestDefaultWrite) => readTestDefault(unwrap<any>((await api.pressureControl.putTestDefault(body)).data)),
    onSuccess: invalidate,
  });
}

export function useDeletePcTestDefault() {
  const invalidate = useInvalidatePc();
  return useMutation({
    mutationFn: async (id: string) => (await api.pressureControl.deleteTestDefault(id)).data,
    onSuccess: invalidate,
  });
}

/** WL-0 (api#1333): every component class, its certificate rule if any, and the limits a new certificate would use. */
export function usePcClassRules(enabled = true) {
  return useQuery({
    queryKey: [...PC_KEY, 'class-rules'],
    queryFn: async () => unwrapArray<PcClassRuleRow>((await api.pressureControl.classRules()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePutPcClassRule() {
  const invalidate = useInvalidatePc();
  return useMutation({
    mutationFn: async ({ componentClass, body }: { componentClass: string; body: PcClassRuleWrite }) =>
      unwrap<PcClassRule>((await api.pressureControl.putClassRule(componentClass, body)).data),
    onSuccess: invalidate,
  });
}

export function useDeletePcClassRule() {
  const invalidate = useInvalidatePc();
  return useMutation({
    mutationFn: async (componentClass: string) => (await api.pressureControl.deleteClassRule(componentClass)).data,
    onSuccess: invalidate,
  });
}

export function useRecordPcExtension(equipmentId: string) {
  const invalidate = useInvalidatePc();
  return useMutation({
    mutationFn: async (body: PcExtensionWrite) => {
      const data = unwrap<any>((await api.pressureControl.recordExtension(equipmentId, body)).data);
      return readExtension(data?.extension);
    },
    onSuccess: invalidate,
  });
}
